<script>
import CatalogProductSkeleton from "@js/components/Sections/Catalog/ProductItem/CatalogProductSkeleton.vue";
import CatalogProductItem from "@js/components/Sections/Catalog/ProductItem/CatalogProductItem.vue";
import Pagination from "@js/components/Pagination/Pagination.vue";
import {useCatalogStore} from "@js/store/CatalogStore.js";
import OnlyMobile from "@js/components/DynamicComponents/OnlyMobile.vue";
import CatalogMobileFilter from "@js/components/Sections/Catalog/CatalogMobileFilter.vue";
import OnlyDesktop from "@js/components/DynamicComponents/OnlyDesktop.vue";
import CatalogFilter from "@js/components/Sections/Catalog/CatalogFilter.vue";
import NumberFormatComponent from "@js/components/Formatter/NumberFormatComponent.vue";
import CatalogSortBlock from "@js/components/Sections/Catalog/Components/CatalogSortBlock.vue";
import IconPlus from "@js/components/Icons/IconPlus.vue";
import CatalogSelectedFilterTags from "@js/components/Sections/Catalog/Components/CatalogSelectedFilterTags.vue";
import IconTrash from "@js/components/Icons/IconTrash.vue";
import ModalSelect from "@js/components/Input/ModalSelect.vue";
import IconChevronDown from "@js/components/Icons/IconChevronDown.vue";
import MobileTopBar from "@js/components/TopBar/MobileTopBar.vue";
import Breadcrumbs from "@js/components/Breadcrumbs/Breadcrumbs.vue";
import CatalogCollectionItem from "@js/components/Sections/Catalog/ProductItem/CatalogCollectionItem.vue";
import CantFindProduct from "@js/components/Sections/Catalog/Components/CantFindProduct.vue";
import {watch} from "vue";
import IconFilter from "@js/components/Icons/IconFilter.vue";
import IconSimpleFilter from "@js/components/Icons/IconSimpleFilter.vue";
import ThreeDView from "@js/components/Sections/3DView/ThreeDView.vue";
import CatalogCategoryNav from "@js/components/Sections/Catalog/Components/CatalogCategoryNav.vue";

export default {
    name: "CatalogComponent",
    props: {
        catalog_title: {
            required: false,
            type: String,
        }
    },
    components: {
        CatalogCategoryNav,
        ThreeDView,
        IconSimpleFilter,
        IconFilter,
        CantFindProduct,
        CatalogCollectionItem,
        Breadcrumbs,
        MobileTopBar,
        IconChevronDown,
        ModalSelect,
        IconTrash,
        CatalogSelectedFilterTags,
        IconPlus,
        CatalogSortBlock,
        NumberFormatComponent,
        CatalogFilter,
        OnlyDesktop,
        CatalogMobileFilter,
        OnlyMobile,
        Pagination,
        CatalogProductItem,
        CatalogProductSkeleton
    },
    watch: {
        'catalogStore.filter': {
            handler: function () {
                if (!this.catalogStore.is_filter_watcher_on) {
                    return;
                }

                clearTimeout(this.getCatalogTimeout);
                this.getCatalogTimeout = setTimeout(() => {
                    this.catalogStore.pagination.current_page = this.catalogStore.getParamFromUrl('page', 1);
                    this.catalogStore.getFilterData();
                }, 300);
            },
            deep: true,
        },
        'catalogStore.products': {
            handler: function () {
                this.insertSchemaMarkup();
            },
            deep: true,
        }
    },
    methods: {
        insertSchemaMarkup() {
            if (this.catalogStore.catalog_type === 'tile' && typeof window !== "undefined") {
                this.removeSchemaMarkup()

                const createScript = (id, data) => {
                    const script = document.createElement("script")
                    script.id = id
                    script.type = "application/ld+json"
                    script.textContent = JSON.stringify(data)
                    document.head.appendChild(script)
                }

                const products = this.catalogStore.products ?? []

                // Дата окончания действия цены — 31 декабря текущего года
                const currentYear = new Date().getFullYear()
                const priceValidUntil = `${currentYear}-12-31`

                // --- JSON-LD для Google (Product)
                const productGraph = products.map((product) => ({
                    "@type": "Product",
                    "name": product.title ?? "Продукт",
                    "description": product.description ?? "Описание отсутствует",
                    "image": product.image_urls?.[0] ?? "/assets/images/defaults/product-not-found.webp",
                    "sku": product.article ?? "Не указан",
                    "offers": {
                        "@type": "Offer",
                        "priceCurrency": "RUB",
                        "price": product.price ?? "0",
                        "priceValidUntil": priceValidUntil,
                        "availability": "https://schema.org/InStock",
                        "url": route('product', { slug: product.slug })
                    },
                    "aggregateRating": {
                        "@type": "AggregateRating",
                        "ratingValue": product.rating_value ?? "4.5",
                        "reviewCount": product.review_count ?? "10"
                    },
                    "review": {
                        "@type": "Review",
                        "reviewRating": {
                            "@type": "Rating",
                            "ratingValue": "5",
                            "bestRating": "5"
                        },
                        "author": {
                            "@type": "Person",
                            "name": "Пользователь"
                        }
                    }
                }))

                createScript("schema-product-jsonld", {
                    "@context": "https://schema.org",
                    "@graph": productGraph
                })

                // --- JSON-LD для Яндекса (OfferCatalog)
                const offerCatalog = {
                    "@context": "https://schema.org/",
                    "@type": "OfferCatalog",
                    "name": this.catalog_title ?? 'Каталог плитки',
                    "url": "https://moda-ceramics.store/catalog",
                    "itemListElement": products.map(product => ({
                        "@type": "Offer",
                        "name": product.title ?? "Плитка",
                        "price": product.price ?? "0",
                        "priceCurrency": "RUB",
                        "availability": "https://schema.org/InStock",
                        "url": route('product', { slug: product.slug })
                    }))
                }

                createScript("schema-catalog-jsonld", offerCatalog)
            }
        },
        removeSchemaMarkup() {
            const ids = ["schema-product-jsonld", "schema-catalog-jsonld"]
            ids.forEach(id => {
                const el = document.getElementById(id)
                if (el) el.remove()
            })
        }
    },
    mounted() {
        watch(
            () => this.catalogStore.catalog_type,
            () => {
                this.catalogStore.resetFilter()
            }
        )
        //     router.on('before', (event) => {
        //         const currentPath = window.location.pathname.split('/').filter(Boolean);
        //         const nextPath = event.detail.visit.url.pathname.split('/').filter(Boolean);
        //
        //         // Проверяем, что остаемся в каталоге
        //         if (currentPath[0] === 'catalog' && nextPath[0] === 'catalog') {
        //             // Если вторая часть разная — значит смена категории
        //             if (currentPath[1] !== nextPath[1]) {
        //                 this.catalogStore.resetFilter();
        //             }
        //         }
        //     })
    },
    beforeMount() {
        //Вставляем микроразметку
        this.removeSchemaMarkup()
        //Сбрасываем фильтр
        this.catalogStore.resetFilter()
        //Синхронизация с URL
        this.catalogStore.syncUrlParamsWithState();
        //Получаем данные каталог и фильтра
        this.catalogStore.getFilterData().then(() => {
            this.catalogStore.is_filter_watcher_on = true;
        })
    },
    setup() {
        const catalogStore = useCatalogStore()
        catalogStore.is_filter_watcher_on = false;
        return {catalogStore}
    }
}
</script>

<template>
    <OnlyMobile :maxSize="1024">
        <CatalogMobileFilter/>
    </OnlyMobile>
    <div class="container">
        <OnlyMobile :maxSize="1024">
            <Breadcrumbs/>
            <div class="flex items-center justify-between mb-3">
                <h1 v-if="catalog_title" class="font-semibold text-lg">
                    {{ catalog_title }}
                </h1>
                <div class="flex items-center gap-1 font-medium"
                     v-if="catalogStore.loadings.catalog_loaded">
                    Найдено:
                    <div v-if="catalogStore.total_result && catalogStore.total_result > 0">
                        <NumberFormatComponent :number="catalogStore.total_result"/>
                    </div>
                </div>
                <div v-else>
                    <div role="status" class="max-w-sm animate-pulse">
                        <div class="h-6 bg-gray-300 rounded w-24 animate-pulse"></div>
                    </div>
                </div>
            </div>
        </OnlyMobile>
        <div class="grid grid-cols-12 gap-y-10 md:gap-x-10 text-black">
            <div class="hidden md:block col-span-12 md:col-span-4 lg:col-span-3">
                <OnlyDesktop>
                    <CatalogFilter class="mb-10"/>
                </OnlyDesktop>
            </div>
            <div class="col-span-12 md:col-span-8 lg:col-span-9 flex flex-col gap-3 md:gap-5">
                <div class="col-span-12">
                    <OnlyDesktop :maxSize="1024">
                        <div class="col-span-12 font-medium">
                            <Breadcrumbs class="-mb-2"/>
                            <h1 v-if="catalog_title" class="font-semibold text-2xl -mb-2">
                                {{ catalog_title }}
                            </h1>
                            <CatalogCategoryNav class="mt-5"/>
                        </div>
                    </OnlyDesktop>
                    <OnlyMobile :maxSize="1024">
                        <CatalogCategoryNav/>
                    </OnlyMobile>
                    <OnlyMobile :maxSize="1024" v-if="catalogStore.selected_filters.length > 0">
                        <div class="flex items-start mb-2 mt-5">
                            <div>
                                <div @click.prevent="catalogStore.resetFilter"
                                     class="flex items-center gap-2 text-xs text-nowrap font-semibold cursor-pointer py-2 px-4 rounded md:rounded-full bg-gray-300 md:hover:bg-baseColor md:hover:text-black">
                                    <IconTrash class="w-4 h-4 text-gray-700 group-hover:text-black transition"/>
                                    Очистить все
                                </div>
                            </div>
                            <div class="ms-2">
                                <div class="vertical-divider"></div>
                            </div>
                            <div class="flex items-start overflow-x-auto">
                                <CatalogSelectedFilterTags/>
                            </div>
                        </div>
                    </OnlyMobile>
                </div>
                <div v-if="!catalogStore.loadings.filter_first_load"
                     role="status" class="md:max-w-xs flex space-x-2 animate-pulse">
                    <div class="h-10 flex-1 rounded-lg bg-gray-200 dark:bg-gray-700"></div>
                    <div class="h-10 flex-1 rounded-lg bg-gray-200 dark:bg-gray-700"></div>
                </div>
                <div v-else
                     class="col-span-12 font-medium mb-5 md:mb-0">
                    <div class="max-sm:w-full inline-flex bg-gray-200 text-black rounded-lg">
                        <button
                            @click.prevent="catalogStore.catalog_type = 'collection'"
                            class="w-full px-10 py-2.5 text-sm rounded-lg focus:outline-none transition"
                            :class="[catalogStore.catalog_type === 'collection'
                                            ? 'bg-baseColor border border-gray-300'
                                            : 'bg-gray-200',
                                        ]">
                            Коллекции
                        </button>
                        <button
                            @click.prevent="catalogStore.catalog_type = 'tile'"
                            class="w-full px-10 py-2.5 text-sm rounded-lg focus:outline-none transition"
                            :class="[
                                          catalogStore.catalog_type === 'tile'
                                            ? 'bg-baseColor border border-gray-300'
                                            : 'bg-gray-200',
                                        ]">
                            Товары
                        </button>
                    </div>
                </div>
                <div class="col-span-12 flex items-center justify-between">
                    <CatalogSortBlock/>
                    <OnlyDesktop class="w-min">
                        <ModalSelect
                            buttonClass="border border-gray-300 bg-transparent text-gray-600 rounded text-sm px-3 py-1">
                            <template #button>
                                {{ catalogStore.filter.per_page }}
                                <IconChevronDown class="w-4 h-4 text-gray-600 ms-1"/>
                            </template>
                            <template #menu>
                                <ul class="py-2 text-sm text-gray-700">
                                    <li v-for="per_page in catalogStore.per_page_array"
                                        @click.prevent="catalogStore.changePerPage(per_page)"
                                        class="block px-4 py-2 font-medium cursor-pointer md:hover:bg-grayBg">
                                        <div class="text-xs font-bold text-nowrap">
                                            {{ per_page }} товаров
                                        </div>
                                    </li>
                                </ul>
                            </template>
                        </ModalSelect>
                    </OnlyDesktop>
                    <OnlyMobile :maxSize="1024"
                                class="inline-flex items-center text-sm font-bold bg-baseColor text-black px-7 py-1.5 rounded leading-tight"
                                @click.prevent="catalogStore.toggleFilter">
                        <IconSimpleFilter class="min-h-4 max-h-4 min-w-4 max-w-4 me-2"/>
                        Фильтр
                        <span v-if="catalogStore.selected_filters.length" class="ms-1">
                                ({{ catalogStore.selected_filters.length }})
                            </span>
                    </OnlyMobile>
                </div>
                <template v-if="catalogStore.selected_filters.length">
                    <OnlyDesktop :maxSize="1024" class="col-span-12">
                        <div
                            class="w-full p-0 md:px-5 md:py-2 md:bg-white md:border md:border-gray-200 md:shadow rounded-xl">
                            <div class="flex items-center flex-wrap">
                                <div @click.prevent="catalogStore.resetFilter"
                                     class="group flex items-center justify-center transition cursor-pointer w-[40px] h-[40px] p-0.5 border border-gray-800 md:hover:bg-baseColor md:hover:border-baseColor rounded-full">
                                    <IconTrash class="w-5 h-5 text-gray-700 group-hover:text-black transition"/>
                                </div>
                                <div class="ms-3 me-1">
                                    <div class="vertical-divider"></div>
                                </div>
                                <CatalogSelectedFilterTags/>
                            </div>
                        </div>
                    </OnlyDesktop>
                </template>

                <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-5">
                    <template v-if="catalogStore.loadings.catalog_loaded">
                        <template v-if="catalogStore.catalog_type === 'collection'">
                            <CatalogCollectionItem v-for="collection in catalogStore.collections"
                                                   :key="collection.id"
                                                   :collection="collection"/>
                        </template>
                        <template v-else>
                            <CatalogProductItem v-for="product in catalogStore.products"
                                                :data="product" :key="product.id"/>
                        </template>
                    </template>
                    <template v-else>
                        <CatalogProductSkeleton v-for="i in 50"/>
                    </template>
                </div>
                <div v-if="catalogStore.loadings.catalog_loaded"
                     class="row d-flex align-items-center justify-content-center">
                    <CantFindProduct class="mb-5"/>
                    <Pagination v-if="catalogStore.pagination.total_pages"
                                :current-page="catalogStore.pagination.current_page"
                                :total-pages="catalogStore.pagination.total_pages"
                                @update:current-page="catalogStore.onPageChange"/>
                </div>
            </div>
        </div>
    </div>
    <ThreeDView/>
</template>

<style scoped lang="scss">
.vertical-divider {
    width: 2px;
    border-radius: 100px;
    height: 2rem;
    background-color: var(--main-color);
}
</style>
